import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './landing.css';
import initLanding from './landingBehaviour';
import { APPS_PUBLISHED, INSTAGRAM_URL } from '../../config/landingFlags';
import heroLandscape from '../../assets/app-landing/landing_landscape.mp4';
import heroPortrait from '../../assets/app-landing/landing_portrait.mp4';
import heroPosterLandscape from '../../assets/app-landing/landing_poster_landscape.jpg';
import heroPosterPortrait from '../../assets/app-landing/landing_poster.jpg';
import logoHorizontal from './assets/padelmvp_horizontal.svg';
import bothWatches from './assets/both_watches.png';
import nextUpFill from './assets/next_up_fill.jpg';
import downloadApp from './assets/download_app.png';
import appStore from './assets/app_store.svg';
import googlePlay from './assets/google_play.svg';
import footerLogo from './assets/footer_logo.svg';
import iconCampaign from './assets/campaign.svg';
import iconWavingHand from './assets/waving_hand.svg';
import iconStar from './assets/star.svg';
import iconTrophy from './assets/trophy.svg';
import playerChris from './assets/player_chris.png';
import avatarAisha from './assets/avatar_aisha.jpg';
import avatarMateo from './assets/avatar_mateo.jpg';
import avatarSiobhan from './assets/avatar_siobhan.jpg';
import avatarKenji from './assets/avatar_kenji.jpg';
import avatarChris from './assets/avatar_chris.jpg';
import avatarElliot from './assets/avatar_elliot.jpg';
import avatarColdA from './assets/avatar_2.jpg';
import avatarColdB from './assets/avatar_3.jpg';

/**
 * Padel MVP marketing landing page (padelmvp.co.uk) — shown to signed-out users.
 * Ported from the pixel-matched static build in /LANDING PAGE (Figma 746:11267 / 751:7831).
 */
const MOBILE_QUERY = '(max-width: 899px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Live media query value, e.g. to pick the portrait or landscape hero video
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export default function LandingPage({ onOpenAuth }) {
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const introRef = useRef(null);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  useEffect(() => initLanding(rootRef.current), []);

  // The hero video runs from the top of the page to the bottom of the intro, so its fade
  // into the page colour lands under the GET STARTED button at every width (Figma 863:3192)
  useLayoutEffect(() => {
    const hero = heroRef.current;
    const intro = introRef.current;
    if (!hero || !intro || typeof ResizeObserver === 'undefined') return undefined;
    const fit = () => { hero.style.height = `${intro.offsetTop + intro.offsetHeight}px`; };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(intro);
    return () => ro.disconnect();
  }, []);

  // Sign-in links (nav, drawer, footer) open the auth modal instead of navigating
  const openAuth = (e) => {
    e.preventDefault();
    onOpenAuth();
  };

  return (
    <div className="lp js" ref={rootRef}>
      <div className="page" id="top">
        {/* Hero video under a scrim (Figma 863:3192). The files have no audio track and play muted. */}
        <div className="hero-bg" aria-hidden="true" ref={heroRef}>
          <video
            key={isMobile ? 'portrait' : 'landscape'}
            src={isMobile ? heroPortrait : heroLandscape}
            poster={isMobile ? heroPosterPortrait : heroPosterLandscape}
            autoPlay={!reduceMotion}
            muted
            loop
            playsInline
            preload={reduceMotion ? 'none' : 'auto'}
            disablePictureInPicture
            disableRemotePlayback
            tabIndex={-1}
          />
        </div>

        {/* Top nav: tabs on desktop, hamburger + drawer on mobile */}
        <nav className="topnav" aria-label="Primary">
          <button className="hamburger" type="button" aria-label="Open menu" aria-expanded="false" aria-controls="drawer">
            <span></span><span></span><span></span>
          </button>
          <div className="tabs">
            <div className="tabs__group">
              <a className="tab is-active" href="#top" data-target="top"><span className="tab__label">ABOUT</span></a>
              <a className="tab" href="#features" data-target="features"><span className="tab__label">FEATURES</span></a>
              {APPS_PUBLISHED ? (
                <a className="tab" href="#download" data-target="download"><span className="tab__label">DOWNLOAD</span></a>
              ) : (
                <a className="tab" href="#signin" onClick={openAuth}><span className="tab__label">SIGN IN</span></a>
              )}
              <a className="tab" href="#contact" data-target="contact"><span className="tab__label">CONTACT</span></a>
            </div>
          </div>
          <div className="divider"></div>
        </nav>

        <div className="drawer" id="drawer" aria-hidden="true">
          <div className="drawer__scrim" data-close></div>
          <div className="drawer__panel" role="dialog" aria-modal="true" aria-label="Menu">
            <img className="drawer__logo" src={logoHorizontal} alt="Padel MVP" width="270" height="52" />
            <a className="drawer__item is-active" href="#top" data-target="top">About</a>
            <a className="drawer__item" href="#features" data-target="features">Features</a>
            {APPS_PUBLISHED ? (
              <a className="drawer__item" href="#download" data-target="download">Download</a>
            ) : (
              <a className="drawer__item" href="#signin" onClick={openAuth}>Sign in</a>
            )}
            <a className="drawer__item" href="#contact" data-target="contact">Contact</a>
          </div>
        </div>

        {/* Header (Figma 746:10541) */}
        <header className="header">
          <img className="header__logo" src={logoHorizontal} alt="Padel MVP logo" width="270" height="52" />
        </header>

        <main className="content">
          {/* Intro (Figma 863:3216): copy over the hero video */}
          <section className="row row--intro" ref={introRef}>
            <div className="row__text">
              <h1 className="title-32">Track every score. <span className="nowrap">Analyse every stat.</span><br />Crown your MVP</h1>
              <p className="body-18 row--intro__body">The all-in-one companion for padel clubs and social groups. Keep real-time score on your smartwatch and track season-long bragging rights with in-depth stats and leaderboards.</p>
              {!APPS_PUBLISHED && (
                <button className="btn-gold row--intro__cta" type="button" onClick={onOpenAuth}>GET STARTED</button>
              )}
            </div>
          </section>

          {/* Hidden until the apps are published (see config/landingFlags) */}
          {APPS_PUBLISHED && (
            <>
            {/* Keep score (Figma 751:13585) */}
            <section className="row row--score" id="features" data-reveal>
              <div className="row__image row__image--score">
                <img src={bothWatches} alt="Live padel score on a Pixel Watch and an Apple Watch" width="1101" height="799" />
              </div>
              <div className="row__text">
                <h2 className="title-32">Keep score without breaking concentration</h2>
                <p className="body-18">Leave your phone in your bag. With standalone integration for Apple Watch and Wear OS, logging points and sets takes a single tap between rallies. Everything syncs instantly with your group, so the court and sideline always know the exact score.</p>
              </div>
            </section>

            {/* CTAs (Figma 751:13680) */}
            <section className="row row--cta" aria-label="Get started">
              <button className="btn-gold" type="button" onClick={onOpenAuth}>GET STARTED</button>
              <a className="btn-gold" href="#download">DOWNLOAD APP</a>
            </section>
            </>
          )}

          {/* Carousel (Figma 751:2804, 751:3086, 751:12506) */}
          <section className="row row--dark carousel" id={APPS_PUBLISHED ? undefined : 'features'} aria-roledescription="carousel" aria-label="App features" data-reveal>
            <div className="carousel__stage">
              <div className="slide-visual slide-visual--standings is-active" data-slide="0">
                <div className="standings" role="table" aria-label="Session standings">
                  <div className="standings__head" role="row">
                    <span className="standings__player" role="columnheader">PLAYER</span>
                    <span className="standings__nums"><span role="columnheader">W</span><span role="columnheader">L</span><span role="columnheader">%</span></span>
                  </div>
                  <div className="standings__row" role="row" style={{ '--o': 1 }}>
                    <span className="standings__player" role="cell"><img src={avatarAisha} alt="" /><span className="standings__name">Aisha Khan</span></span>
                    <span className="standings__nums"><span role="cell" data-count="10">10</span><span role="cell" data-count="4">4</span><span role="cell" data-count="71.4" data-suffix="%">71.4%</span></span>
                  </div>
                  <div className="standings__row" role="row" style={{ '--o': 1 }}>
                    <span className="standings__player" role="cell"><img src={avatarMateo} alt="" /><span className="standings__name">Mateo Rodriguez</span></span>
                    <span className="standings__nums"><span role="cell" data-count="9">9</span><span role="cell" data-count="5">5</span><span role="cell" data-count="64.3" data-suffix="%">64.3%</span></span>
                  </div>
                  <div className="standings__row" role="row" style={{ '--o': 1 }}>
                    <span className="standings__player" role="cell"><img src={avatarSiobhan} alt="" /><span className="standings__name">Siobhan Murphy</span></span>
                    <span className="standings__nums"><span role="cell" data-count="9">9</span><span role="cell" data-count="5">5</span><span role="cell" data-count="64.3" data-suffix="%">64.3%</span></span>
                  </div>
                  <div className="standings__row" role="row" style={{ '--o': 0.5 }}>
                    <span className="standings__player" role="cell"><img src={avatarKenji} alt="" /><span className="standings__name">Kenji Tanaka</span></span>
                    <span className="standings__nums"><span role="cell" data-count="9">9</span><span role="cell" data-count="6">6</span><span role="cell" data-count="60.0" data-suffix="%">60.0%</span></span>
                  </div>
                  <div className="standings__row" role="row" style={{ '--o': 0.1 }}>
                    <span className="standings__player" role="cell"><img src={avatarChris} alt="" /><span className="standings__name">Chris Hurst</span></span>
                    <span className="standings__nums"><span role="cell" data-count="4">4</span><span role="cell" data-count="10">10</span><span role="cell" data-count="28.6" data-suffix="%">28.6%</span></span>
                  </div>
                </div>
              </div>

              <div className="slide-visual slide-visual--cards" data-slide="1" aria-hidden="true">
                <ul className="insights">
                  <li className="insight">
                    <span className="insight__badge" style={{ '--badge': '#7631d6' }}><span className="msr">swords</span></span>
                    <span className="insight__text">
                      <span className="insight__title">Deadly Duo</span>
                      <span className="insight__player">Aisha &amp; Elliot</span>
                      <span className="insight__data">12 Wins | 76.5% Win Rate</span>
                    </span>
                    <span className="insight__avatars insight__avatars--duo"><img src={avatarAisha} alt="" /><img src={avatarElliot} alt="" /></span>
                  </li>
                  <li className="insight">
                    <span className="insight__badge" style={{ '--badge': '#df582b' }}><span className="msr">mode_heat</span></span>
                    <span className="insight__text">
                      <span className="insight__title">Hot Streak</span>
                      <span className="insight__player">Kenji Tanaka</span>
                      <span className="insight__data">10 wins | 71.4% win rate</span>
                    </span>
                    <span className="insight__avatars"><img src={avatarKenji} alt="" /></span>
                  </li>
                  <li className="insight">
                    <span className="insight__badge" style={{ '--badge': '#2569e8' }}><span className="msr">padel</span></span>
                    <span className="insight__text">
                      <span className="insight__title">Ace Machine</span>
                      <span className="insight__player">Mateo Rodriguez</span>
                      <span className="insight__data">6 wins as server | 85.7% win rate</span>
                    </span>
                    <span className="insight__avatars"><img src={avatarMateo} alt="" /></span>
                  </li>
                  <li className="insight">
                    <span className="insight__badge" style={{ '--badge': '#31b3d6' }}><span className="msr">ac_unit</span></span>
                    <span className="insight__text">
                      <span className="insight__title">Cold Streak</span>
                      <span className="insight__player">Siobhan Murphy</span>
                      <span className="insight__data">54 Wins | 58.1% Win Rate</span>
                    </span>
                    <span className="insight__avatars insight__avatars--duo"><img src={avatarColdA} alt="" /><img src={avatarColdB} alt="" /></span>
                  </li>
                </ul>
              </div>

              <div className="slide-visual slide-visual--player" data-slide="2" aria-hidden="true">
                <div className="player-card">
                  <img className="player-card__avatar" src={playerChris} alt="" width="80" height="80" />
                  <h3 className="player-card__name">Chris Hurst</h3>
                  <div className="player-card__grid">
                    <div className="player-card__stat"><span className="player-card__label">Wins</span><span className="player-card__value" data-count="43">43</span></div>
                    <div className="player-card__stat"><span className="player-card__label">Losses</span><span className="player-card__value" data-count="36">36</span></div>
                    <div className="player-card__stat"><span className="player-card__label">Win %</span><span className="player-card__value" data-count="54.4" data-suffix="%">54.4%</span></div>
                    <div className="player-card__stat"><span className="player-card__label">Longest streak</span><span className="player-card__value" data-count="8">8</span></div>
                    <div className="player-card__stat"><span className="player-card__label">MVPs</span><span className="player-card__value" data-count="2">2</span></div>
                    <div className="player-card__stat"><span className="player-card__label">Event Wins</span><span className="player-card__value" data-count="1">1</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="carousel__side">
              <div className="carousel__texts" aria-live="polite">
                <div className="slide-text is-active" data-slide="0" role="group" aria-roledescription="slide" aria-label="1 of 3">
                  <h2 className="title-48">Match, event and overall standings</h2>
                  <p className="body-18 primary">Turn weekly knockabouts into a serious title race. Real-time tables, match summaries, and event leaderboards updated the second the final point is scored.</p>
                </div>
                <div className="slide-text" data-slide="1" role="group" aria-roledescription="slide" aria-label="2 of 3" aria-hidden="true">
                  <h2 className="title-48">Club insights</h2>
                  <p className="body-18 primary">Discover the stories behind every scoreline. Spot who’s riding a hot streak, who’s frozen on a cold run, who’s racking up aces, and who’s earned their place in your club’s Hall of Fame.</p>
                </div>
                <div className="slide-text" data-slide="2" role="group" aria-roledescription="slide" aria-label="3 of 3" aria-hidden="true">
                  <h2 className="title-48">Player stats</h2>
                  <p className="body-18 primary">Every player gets their own dedicated profile card. Dive into lifetime win rates, total matches played, head-to-head records, and recent form to track your progress and settle court debates.</p>
                </div>
              </div>

              <div className="carousel__controls">
                <button className="icon-btn" type="button" data-dir="-1" aria-label="Previous slide"><span className="msr">chevron_left</span></button>
                <div className="dots" role="tablist" aria-label="Choose slide">
                  <button className="dot is-active" type="button" role="tab" aria-selected="true" aria-label="Slide 1"><span className="dot__fill"></span></button>
                  <button className="dot" type="button" role="tab" aria-selected="false" aria-label="Slide 2"><span className="dot__fill"></span></button>
                  <button className="dot" type="button" role="tab" aria-selected="false" aria-label="Slide 3"><span className="dot__fill"></span></button>
                </div>
                <button className="icon-btn" type="button" data-dir="1" aria-label="Next slide"><span className="msr">chevron_right</span></button>
              </div>
            </div>
          </section>

          {/* Rotation mode (Figma 746:11720) */}
          <section className="row row--rotation" data-reveal>
            <div className="row__text row__text--flush">
              <h2 className="title-32">Odd numbers? No problem. Meet Rotation Mode.</h2>
              <p className="body-18">Got 5 players for doubles or 3 for singles? Stop worrying about who sits out next. Our smart rotation algorithm creates a fair, seamless Round Robin where players cycle in and out automatically after each game.</p>
            </div>
            <div className="row__image row__image--nextup">
              <img src={nextUpFill} alt="Next up: Kenji serving, Elliot sitting out" width="1834" height="824" />
            </div>
          </section>

          {APPS_PUBLISHED ? (
            <>
            {/* Download (Figma 751:7629) */}
            <section className="row row--download" id="download" data-reveal>
              <div className="row__phone">
                <img className="phone-png" src={downloadApp} alt="Coastal Smash club screen in the Padel MVP app" width="1048" height="948" />
              </div>
              <div className="row__text row__text--flush">
                <h2 className="title-32">Download the app</h2>
                <p className="body-18">Available on iOS and Android with companion apps for your wrist. Set up your club, invite your mates, and elevate your next padel session with Padel MVP.</p>
                <div className="stores">
                  <a className="store" href="#" aria-label="Download on the App Store"><img src={appStore} alt="" width="120" height="40" /></a>
                  <a className="store" href="#" aria-label="Get it on Google Play"><img src={googlePlay} alt="" width="120" height="40" /></a>
                </div>
              </div>
            </section>
            </>
          ) : (
            /* Coming soon (Figma 753:13982) */
            <section className="row row--score row--coming-soon" id="coming-soon" data-reveal>
              <div className="row__image row__image--score">
                <img src={bothWatches} alt="Padel MVP score on a Pixel Watch and an Apple Watch" width="1101" height="799" />
              </div>
              <div className="row__text">
                <h2 className="title-32">Coming soon!</h2>
                <p className="body-18">
                  Dedicated iOS and Android apps complete with companion apps for WearOS &amp; Apple Watch.
                  <br />
                  <br />
                  Watch this space.
                </p>
              </div>
            </section>
          )}

          {/* More + footer (Figma 751:3325) */}
          <section className="row row--dark row--more" id="contact">
            <h2 className="title-48 more__title" data-reveal>Wait, there’s more...</h2>
            <ul className="features" data-reveal>
              <li className="feature">
                <span className="feature__icon"><img src={iconCampaign} alt="" width="52" height="40" /></span>
                <h3 className="feature__title">Scores read aloud</h3>
                <p className="body-18 primary">Scores can be read aloud with on device text-to-speech. Recommend pairing with a Bluetooth speaker so all players can hear.</p>
              </li>
              <li className="feature">
                <span className="feature__icon"><img src={iconWavingHand} alt="" width="56" height="56" /></span>
                <h3 className="feature__title">Hand tracking</h3>
                <p className="body-18 primary">Don’t have a watch? Try out our experimental feature that allows you to control scores with hand gestures during games.</p>
              </li>
              <li className="feature">
                <span className="feature__icon"><img src={iconStar} alt="" width="46" height="44" /></span>
                <h3 className="feature__title">Golden point</h3>
                <p className="body-18 primary">Full support for classic advantage scoring or high-stakes sudden-death Golden Points (Punto de Oro) with a single tap.</p>
              </li>
              <li className="feature">
                <span className="feature__icon"><img src={iconTrophy} alt="" width="48" height="46" /></span>
                <h3 className="feature__title">Custom events</h3>
                <p className="body-18 primary">As well as tracking matches and all time stats, you can also create custom events to keep things competitive for your club. Most match wins takes the title.</p>
              </li>
            </ul>

            <div className="divider"></div>

            {APPS_PUBLISHED ? (
              <footer className="footer">
                <img className="footer__logo" src={footerLogo} alt="Padel MVP" width="300" height="226" />
                <div className="footer__body">
                  <div className="footer__links">
                    <nav className="footer__col" aria-label="Follow us">
                      <h3 className="footer__heading">Follow us</h3>
                      <a href="#">Instagram</a>
                      <a href="#">Facebook</a>
                    </nav>
                    <nav className="footer__col" aria-label="Padel MVP">
                      <h3 className="footer__heading">Padel MVP</h3>
                      <a href="#download">Download our app</a>
                      <a href="#top">About us</a>
                    </nav>
                    <nav className="footer__col" aria-label="Legal">
                      <h3 className="footer__heading">Legal</h3>
                      <a href="#">Privacy policy</a>
                      <a href="#">Cookies policy</a>
                    </nav>
                  </div>
                  <p className="footer__copy">© 2026 Padel MVP. All rights reserved.</p>
                </div>
              </footer>
            ) : (
              /* Simplified footer (Figma 753:14064) */
              <footer className="footer footer--simple">
                <img className="footer__logo" src={footerLogo} alt="Padel MVP" width="300" height="226" />
                <div className="footer__body">
                  <nav className="footer__col" aria-label="Get started">
                    <h3 className="footer__heading">Get started</h3>
                    <a href="#signin" onClick={openAuth}>Login / Sign up</a>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">Follow us on Instagram</a>
                  </nav>
                  <p className="footer__copy">© 2026 Padel MVP. All rights reserved.</p>
                </div>
              </footer>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
